import { useEffect, useState } from "react";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  doc
} from "firebase/firestore";
import { db } from "./firebase";
import { useAuth } from "./auth";
import { useEffectiveTenantId } from "./impersonation";

export type ActiveVoucher = {
  id: string;
  code: string;
  discountPercent: number;
  ambassadorId: string;
  expiresAt: Date;
};

/** Subscribe al voucher activo (no usado, no expirado) del user. */
export function useActiveVoucher(): ActiveVoucher | null {
  const { user } = useAuth();
  const tenantId = useEffectiveTenantId();
  const [voucher, setVoucher] = useState<ActiveVoucher | null>(null);

  useEffect(() => {
    if (!user || !tenantId) {
      setVoucher(null);
      return;
    }
    const q = query(
      collection(db, "tenants", tenantId, "discountVouchers"),
      where("userId", "==", user.uid),
      where("used", "==", false),
      orderBy("expiresAt", "desc")
    );
    return onSnapshot(
      q,
      (snap) => {
        const now = Date.now();
        const fresh = snap.docs
          .map((d) => {
            const x = d.data();
            const exp = (x.expiresAt as { toDate?: () => Date })?.toDate?.();
            return exp && exp.getTime() > now
              ? {
                  id: d.id,
                  code: x.code as string,
                  discountPercent: x.discountPercent as number,
                  ambassadorId: x.ambassadorId as string,
                  expiresAt: exp
                }
              : null;
          })
          .filter((v): v is ActiveVoucher => v !== null);
        setVoucher(fresh[0] ?? null);
      },
      () => setVoucher(null)
    );
  }, [user, tenantId]);

  return voucher;
}

export type AmbassadorDashboardData = {
  loading: boolean;
  ambassador: { id: string; name: string; instagram: string; totalSales: number; totalCommissions: number } | null;
  codes: Array<{ id: string; code: string; discountPercent: number; timesUsed: number; active: boolean }>;
  commissions: Array<{ id: string; createdAt: Date; code: string; saleAmount: number; commissionAmount: number }>;
};

export function useAmbassadorDashboard(): AmbassadorDashboardData {
  const { user, claims } = useAuth();
  const [data, setData] = useState<AmbassadorDashboardData>({
    loading: true,
    ambassador: null,
    codes: [],
    commissions: []
  });

  useEffect(() => {
    if (!user || !claims.tenantId || !claims.ambassadorId) {
      setData((d) => ({ ...d, loading: false }));
      return;
    }
    const tid = claims.tenantId;
    const aid = claims.ambassadorId;

    const unsubAmb = onSnapshot(
      doc(db, "tenants", tid, "ambassadors", aid),
      (snap) => {
        const x = snap.data();
        setData((d) => ({
          ...d,
          loading: false,
          ambassador: x
            ? {
                id: snap.id,
                name: (x.name as string) ?? "",
                instagram: (x.instagram as string) ?? "",
                totalSales: (x.totalSales as number) ?? 0,
                totalCommissions: (x.totalCommissions as number) ?? 0
              }
            : null
        }));
      }
    );

    const unsubCodes = onSnapshot(
      query(
        collection(db, "tenants", tid, "discountCodes"),
        where("ambassadorId", "==", aid)
      ),
      (snap) => {
        setData((d) => ({
          ...d,
          codes: snap.docs.map((doc) => {
            const x = doc.data();
            return {
              id: doc.id,
              code: (x.code as string) ?? doc.id,
              discountPercent: (x.discountPercent as number) ?? 0,
              timesUsed: (x.timesUsed as number) ?? 0,
              active: (x.active as boolean) ?? true
            };
          })
        }));
      }
    );

    const unsubCommissions = onSnapshot(
      query(
        collection(db, "tenants", tid, "commissions"),
        where("ambassadorId", "==", aid),
        orderBy("createdAt", "desc")
      ),
      (snap) => {
        setData((d) => ({
          ...d,
          commissions: snap.docs.map((doc) => {
            const x = doc.data();
            return {
              id: doc.id,
              createdAt: (x.createdAt as { toDate?: () => Date })?.toDate?.() ?? new Date(),
              code: (x.code as string) ?? "",
              saleAmount: (x.saleAmount as number) ?? 0,
              commissionAmount: (x.commissionAmount as number) ?? 0
            };
          })
        }));
      }
    );

    return () => {
      unsubAmb();
      unsubCodes();
      unsubCommissions();
    };
  }, [user, claims.tenantId, claims.ambassadorId]);

  return data;
}

export type TenantInviteRow = {
  id: string;
  email: string;
  role: "tenant_admin" | "ambassador";
  expiresAt: Date | null;
  used: boolean;
  createdAt: Date | null;
  invitedByEmail: string | null;
};

export function useTenantInvites(roleFilter?: "ambassador" | "tenant_admin"): {
  invites: TenantInviteRow[];
  loading: boolean;
} {
  const tenantId = useEffectiveTenantId();
  const [state, setState] = useState<{ invites: TenantInviteRow[]; loading: boolean }>({
    invites: [],
    loading: true
  });

  useEffect(() => {
    if (!tenantId) {
      setState({ invites: [], loading: false });
      return;
    }
    const baseRef = collection(db, "tenants", tenantId, "invites");
    const q = roleFilter
      ? query(baseRef, where("role", "==", roleFilter), orderBy("createdAt", "desc"))
      : query(baseRef, orderBy("createdAt", "desc"));
    return onSnapshot(
      q,
      (snap) => {
        setState({
          loading: false,
          invites: snap.docs.map((d) => {
            const x = d.data();
            return {
              id: d.id,
              email: (x.email as string) ?? "",
              role: (x.role as "tenant_admin" | "ambassador") ?? "ambassador",
              expiresAt: (x.expiresAt as { toDate?: () => Date })?.toDate?.() ?? null,
              used: (x.used as boolean) ?? false,
              createdAt: (x.createdAt as { toDate?: () => Date })?.toDate?.() ?? null,
              invitedByEmail: (x.invitedByEmail as string | null) ?? null
            };
          })
        });
      },
      () => setState((s) => ({ ...s, loading: false }))
    );
  }, [tenantId, roleFilter]);

  return state;
}

export type TenantAmbassadorRow = {
  id: string;
  userId: string;
  name: string;
  instagram: string;
  followers: string | null;
  email: string | null;
  phone: string | null;
  active: boolean;
  totalSales: number;
  totalCommissions: number;
  salesCount: number;
  activeCodes: Array<{ code: string; discountPercent: number; timesUsed: number }>;
  createdAt: Date;
};

export function useTenantAmbassadors(): { ambassadors: TenantAmbassadorRow[]; loading: boolean } {
  const tenantId = useEffectiveTenantId();
  const [state, setState] = useState<{ ambassadors: TenantAmbassadorRow[]; loading: boolean }>({
    ambassadors: [],
    loading: true
  });

  useEffect(() => {
    if (!tenantId) {
      setState({ ambassadors: [], loading: false });
      return;
    }
    const q = query(
      collection(db, "tenants", tenantId, "ambassadors"),
      orderBy("createdAt", "desc")
    );
    let codesByAmb: Record<string, Array<{ code: string; discountPercent: number; timesUsed: number }>> = {};
    let salesCountByAmb: Record<string, number> = {};
    let lastAmbDocs: Array<{ id: string; data: Record<string, unknown> }> = [];

    const render = () => {
      setState({
        loading: false,
        ambassadors: lastAmbDocs.map(({ id, data: x }) => ({
          id,
          userId: (x.userId as string) ?? "",
          name: (x.name as string) ?? "",
          instagram: (x.instagram as string) ?? "",
          followers: (x.followers as string | null) ?? null,
          email: (x.email as string | null) ?? null,
          phone: (x.phone as string | null) ?? null,
          active: (x.active as boolean) ?? true,
          totalSales: (x.totalSales as number) ?? 0,
          totalCommissions: (x.totalCommissions as number) ?? 0,
          salesCount: salesCountByAmb[id] ?? 0,
          activeCodes: codesByAmb[id] ?? [],
          createdAt: (x.createdAt as { toDate?: () => Date })?.toDate?.() ?? new Date()
        }))
      });
    };

    const unsubAmb = onSnapshot(q, (snap) => {
      lastAmbDocs = snap.docs.map((d) => ({ id: d.id, data: d.data() }));
      render();
    }, () => setState((s) => ({ ...s, loading: false })));

    const unsubCodes = onSnapshot(
      query(collection(db, "tenants", tenantId, "discountCodes"), where("active", "==", true)),
      (snap) => {
        const map: typeof codesByAmb = {};
        snap.docs.forEach((d) => {
          const x = d.data();
          const aid = x.ambassadorId as string;
          if (!aid) return;
          if (!map[aid]) map[aid] = [];
          map[aid].push({
            code: (x.code as string) ?? d.id,
            discountPercent: (x.discountPercent as number) ?? 0,
            timesUsed: (x.timesUsed as number) ?? 0
          });
        });
        codesByAmb = map;
        render();
      }
    );

    const unsubComm = onSnapshot(
      collection(db, "tenants", tenantId, "commissions"),
      (snap) => {
        const map: typeof salesCountByAmb = {};
        snap.docs.forEach((d) => {
          const aid = d.data().ambassadorId as string;
          if (!aid) return;
          map[aid] = (map[aid] ?? 0) + 1;
        });
        salesCountByAmb = map;
        render();
      }
    );

    return () => { unsubAmb(); unsubCodes(); unsubComm(); };
  }, [tenantId]);

  return state;
}

export function useAmbassadorApplications(filter: "pending" | "approved" | "rejected" | "all") {
  const tenantId = useEffectiveTenantId();
  const [apps, setApps] = useState<
    Array<{
      id: string;
      name: string;
      email: string;
      instagram: string;
      followers: string | null;
      message: string | null;
      status: string;
      createdAt: Date;
      hasAccount: boolean;
    }>
  >([]);

  useEffect(() => {
    if (!tenantId) return;
    let q = query(
      collection(db, "tenants", tenantId, "ambassadorApplications"),
      orderBy("createdAt", "desc")
    );
    if (filter !== "all") {
      q = query(
        collection(db, "tenants", tenantId, "ambassadorApplications"),
        where("status", "==", filter),
        orderBy("createdAt", "desc")
      );
    }
    return onSnapshot(q, (snap) => {
      setApps(
        snap.docs.map((doc) => {
          const x = doc.data();
          return {
            id: doc.id,
            name: (x.name as string) ?? "",
            email: (x.email as string) ?? "",
            instagram: (x.instagram as string) ?? "",
            followers: (x.followers as string | null) ?? null,
            message: (x.message as string | null) ?? null,
            status: (x.status as string) ?? "pending",
            createdAt: (x.createdAt as { toDate?: () => Date })?.toDate?.() ?? new Date(),
            hasAccount: !!x.userId
          };
        })
      );
    });
  }, [tenantId, filter]);

  return apps;
}
